import isEmpty from "lodash/isEmpty";
import {
  CounterpartyProfileWrite,
  Payer,
  PayerEngagementCreateType,
  PayerUpdate
} from "@wingspanhq/wingspan-api-sdk";
import { linkRequestsApi, payerEngagementsApi, payersApi } from "@api";
import {
  queriesEngagements,
  queriesPayerEngagements,
  queriesPayerRows,
  queriesPayers
} from "@query";
import { useWSMutation, WSMutationConfig } from "../../../query/helpers";
import { isConflictError } from "../../../v3/utils/apiError";
import { WSServiceError } from "../../../utils/serviceHelper";
import { QUERY_PAYER_ROWS } from "../../../query/search/payer/queries/useQueryPayerRows";
import { QUERY_PAYER_ROWS_ALL } from "../../../query/search/payer/queries/useQueryPayerRowsAll";
import { QUERY_PAYER_ROWS_SUMMARY } from "../../../query/search/payer/queries/useQueryPayerRowsSummary";
import { ensureDefaultEngagement } from "../../EngagementTypes/utils/ensureDefaultEngagement";

export type CreateClientInput = {
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  emailsCC?: string[];
};

/**
 * Maps the collected client fields onto the v3 profile. v1 stored one
 * `payerName` string; v3 wants the individual's first/last name separate, so
 * the modal now collects them as distinct fields and passes them through here
 * verbatim (no more lossy whitespace split).
 */
const buildProfile = (
  firstName?: string,
  lastName?: string,
  company?: string
): CounterpartyProfileWrite | undefined => {
  const profile: CounterpartyProfileWrite = {};

  const trimmedFirstName = firstName?.trim();
  const trimmedLastName = lastName?.trim();
  if (trimmedFirstName || trimmedLastName) {
    profile.individual = {
      firstName: trimmedFirstName || undefined,
      lastName: trimmedLastName || undefined
    };
  }

  const trimmedCompany = company?.trim();
  if (trimmedCompany) {
    profile.company = { doingBusinessAs: trimmedCompany };
  }

  return isEmpty(profile) ? undefined : profile;
};

/**
 * A repeat submit resolves to the client that already holds the email, and that
 * client already holds the link request the first submit issued — a second one
 * answers `409 ResourceConflict`. Letting it through would fail the submit on a
 * re-add, which is the case v1 handled by upserting.
 */
const createLinkRequestIgnoringAlreadyRequested = async (
  payerId: string,
  targetEmail: string
): Promise<void> => {
  try {
    await linkRequestsApi.createPayerLinkRequest({
      payerId,
      linkRequestNestedCreate: { targetEmail }
    });
  } catch (error) {
    if (isConflictError(error)) {
      return;
    }
    throw error;
  }
};

/**
 * Attaches the default engagement, unless the client already carries it.
 *
 * The API does not enforce one engagement per (client, template) pair — a
 * repeat create resolves rather than conflicting (WIN-16021) — so the check has
 * to be a read, and it runs on every submit: a repeat submit resolves to the
 * existing client silently, so there is no signal that would let it be skipped
 * for a first-time client. Without the read a re-add leaves the client holding
 * two identical engagements. The conflict is still tolerated, for whenever that
 * server constraint does land.
 */
const attachDefaultEngagement = async (request: {
  payerId: string;
  engagementId: string;
}): Promise<void> => {
  const { data } = await payerEngagementsApi.listPayerEngagements({
    payerId: request.payerId,
    page: { size: 100 }
  });

  const alreadyAttached = data?.some(
    ({ engagementId }) => engagementId === request.engagementId
  );
  if (alreadyAttached) {
    return;
  }

  try {
    await payerEngagementsApi.createPayerEngagement({
      payerId: request.payerId,
      payerEngagementCreate: {
        engagementId: request.engagementId,
        type: PayerEngagementCreateType.COMMERCIAL,
        startDate: new Date()
      }
    });
  } catch (error) {
    if (isConflictError(error)) {
      return;
    }
    throw error;
  }
};

/**
 * Creates a client (payer) on v3, issues the Wingspan invite, and creates the
 * client's engagement.
 *
 * v1 `createPayer` was one call that recorded the payer relationship (with the
 * payee-owned `payerName` / `payerCompanyName` / `payerEmailCC`). v3 splits it:
 * `createPayer` takes only the lean payer (email + profile), and the invite —
 * modeled in the invite-flows spec as a `PayerPayeeLinkRequest` — is now an
 * explicit `createPayerLinkRequest`.
 *
 * `PayerCreate` carries no CC emails, but `PayerUpdate` does
 * (`notificationCcEmails`, WIN-15229 — the same field the edit modal writes), so
 * v1's create-time "Contact email CC" survives as a follow-up patch, issued only
 * when the modal collected any. The QuickBooks mapping v1 also collected here is
 * still dropped: `PayerUpdate` accepts `quickbooksCustomerId`/`quickbooksItemId`,
 * but the customer/item pickers need the QuickBooks connection surface, which is
 * still v1 (and therefore dead). See V3_MIGRATION_TRACKER.md.
 *
 * **The create upserts on the email, but keeps the stored fields.** An address
 * one of the payee's clients already holds resolves to that client (same id, no
 * second row, no error), which is v1's behavior — but the submitted `profile` is
 * dropped, where v1 applied it. The response is indistinguishable from a fresh
 * create, so the profile is always applied with a follow-up `updatePayer`; on a
 * fresh create that rewrites the value the create just stored. The submitted
 * email is never written back — the record's `profile.email` can be its pending
 * invite's target rather than the value submitted, and writing it would retarget
 * a live invite (ARC-980/ARC-981).
 *
 * **The engagement is the third explicit step.** v1 created the payer engagement
 * under the hood; on v3 nothing does, and a client with no engagement cannot be
 * invoiced — `Invoice` requires a `payerEngagementId`, so the assignment picker
 * would be empty. The client side needs no per-client configuration (the payee
 * decides nothing about how the client engages it), so this attaches the
 * account's default engagement template silently rather than asking. The
 * contractor side is the opposite: `CreateContractorModal` requires an explicit
 * engagement choice.
 *
 * Reads stay on v1 (the Clients list/detail are still v1), so the mutation
 * explicitly invalidates the v1 payer caches; the v3 payer created this way
 * surfaces in the v1 `/search/payer-row` list once the backend indexes it.
 */
export const useCreateClient = (
  config?: WSMutationConfig<Payer, WSServiceError, CreateClientInput>
) =>
  useWSMutation<Payer, WSServiceError, CreateClientInput>(
    async input => {
      const profile = buildProfile(
        input.firstName,
        input.lastName,
        input.company
      );

      const payer = await payersApi.createPayer({
        payerCreate: { email: input.email, profile }
      });

      const payerUpdate: PayerUpdate = {
        // Dropped by the create when it resolves to an existing client.
        ...(profile ? { profile } : {}),
        ...(input.emailsCC?.length
          ? { notificationCcEmails: input.emailsCC }
          : {})
      };

      // The engagement the client's invoices are billed against comes back
      // `Created` (not `Activated`) — the pickers treat both as selectable, see
      // `SelectEngagementForPayer`.
      const [, engagement] = await Promise.all([
        createLinkRequestIgnoringAlreadyRequested(payer.id!, input.email),
        ensureDefaultEngagement(),
        // Only patch when there is something to store, so the common case stays
        // at v1's request count.
        isEmpty(payerUpdate)
          ? undefined
          : payersApi.updatePayer({ payerId: payer.id!, payerUpdate })
      ]);

      await attachDefaultEngagement({
        payerId: payer.id!,
        engagementId: engagement.id!
      });

      return payer;
    },
    {
      dependencies: [
        QUERY_PAYER_ROWS,
        QUERY_PAYER_ROWS_ALL,
        QUERY_PAYER_ROWS_SUMMARY,
        queriesPayers.listPayers.queryKeyBase,
        // The clients list and every client picker read the denormalized search
        // rows, so they must refresh too — without this the new client is missing
        // from a list that was already on screen until a hard reload.
        queriesPayerRows.searchPayerRows.queryKeyBase,
        queriesPayerEngagements.listPayerEngagements.queryKeyBase,
        queriesEngagements.listEngagements.queryKeyBase
      ],
      ...config
    }
  );
